import userResources from "api/userResources";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

const passengerTypes = [
  { id: "adt", label: "Adt.", value: "passengers.adt" },
  { id: "chd", label: "Chd.", value: "passengers.chd" },
  { id: "inf", label: "Inf.", value: "passengers.inf" },
];

// Helper to round to 2 decimal places
const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

// Helper to calculate subTotal for a given passenger type
const calculateSubTotal = (base, gst, taxes, count) => {
  const sum = (Number(base) || 0) + (Number(gst) || 0) + (Number(taxes) || 0);
  return roundToTwo(sum * (Number(count) || 1));
};

// Helper to format a number to at most two decimals without unnecessary trailing zeros
const formatDecimal = (num) => {
  const n = Number(num);
  if (isNaN(n)) return "0.00";
  return parseFloat(n.toFixed(2)).toString();
};

export default function Calculation({
  supplierData,
  register,
  errors,
  clearErrors,
  watch,
  setValue,
  control,
  getValues,
  airTicket,
  AirTicketfromPax,
  AirTickettoSupplier,
  insurance,
  insurancePax,
  insuranceSupplier,
  LandPackage,
  landPackagePax,
  landPackageSupplier,
  Cruise,
  cruisePax,
  cruiseSupplier,
  hotel,
  hotelPax,
  hotelSupplier,
  updateData,
}) {
  // const paymentMethodErrorPax =
  //   errors.airticket?.airticket_from_pax?.transation_type_id?.message ||
  //   errors.insurance?.insurance_from_pax?.transation_type_id?.message ||
  //   errors.land_package?.landpackage_from_pax?.transation_type_id?.message;
  // const paymentMethodErrorSupplier =
  //   errors.airticket?.airticket_to_supplier?.transation_type_id?.message ||
  //   errors.insurance?.insurance_to_supplier?.transation_type_id?.message ||
  //   errors.land_package?.landpackage_to_supplier?.transation_type_id?.message;

  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const getTransactionType = async () => {
    try {
      const response = await userResources.transactionType();
      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setPaymentMethodData(combinedData);
    } catch (error) {
      console.log(error);
    }
  };

  // Define a common prefix for Pax calculations based on your conditions.
  const prefixPax = AirTicketfromPax
    ? "airticket.airticket_from_pax"
    : insurancePax
    ? "insurance.insurance_from_pax"
    : landPackagePax
    ? "land_package.landpackage_from_pax"
    : cruisePax
    ? "cruise.cruise_from_pax"
    : hotelPax
    ? "hotel.hotel_from_pax"
    : "";

  const passengersprefixPax = AirTicketfromPax
    ? "airticket.passengers"
    : insurancePax
    ? "insurance.passengers"
    : landPackagePax
    ? "land_package.passengers"
    : cruisePax
    ? "cruise.passengers"
    : hotelPax
    ? "hotel.passengers"
    : "";

  // Watch total and amount paid for Pax
  const totalPax = watch(`${prefixPax}.total`);
  const totalpaxbalance = watch(`${prefixPax}.balance`);
  const totalAmountPaidPax = watch(`${prefixPax}.amountPaid`);
  const totalRefundPax = watch(`${prefixPax}.refund`);
  const refundPaid = watch(`${prefixPax}.refund_paid`);

  useEffect(() => {
    const totalValuePax = Number(totalPax) || 0;
    const paidValuePax = Number(totalAmountPaidPax) || 0;
    const refundValuePax = Number(totalRefundPax) || 0;
    const refundPaidValuePax = Number(refundPaid) || 0;

    // Calculate actual refund pending
    const refundPending = refundValuePax - refundPaidValuePax;

    // Final balance
    const balancePax = roundToTwo(totalValuePax - paidValuePax - refundPending);

    setValue(`${prefixPax}.balance`, balancePax);
  }, [
    totalPax,
    totalAmountPaidPax,
    totalRefundPax,
    refundPaid,
    setValue,
    prefixPax,
  ]);

  // Adult fields (Pax)
  const baseFareAdtPax = watch(`${prefixPax}.baseFare.adt1`, 0);
  const gstHstAdtPax = watch(`${prefixPax}.gstHst.adt1`, 0);
  const taxesAdtPax = watch(`${prefixPax}.taxes.adt1`, 0);
  const totalAdtPax = watch(`${passengersprefixPax}.adt`, 1); // defaults to 1
  const subTotalAdtPax = watch(`${prefixPax}.subTotal.adt1`, 0);

  useEffect(() => {
    const newSubTotal = calculateSubTotal(
      baseFareAdtPax,
      gstHstAdtPax,
      taxesAdtPax,
      totalAdtPax
    );
    setValue(`${prefixPax}.subTotal.adt1`, newSubTotal);
  }, [
    baseFareAdtPax,
    gstHstAdtPax,
    taxesAdtPax,
    totalAdtPax,
    setValue,
    prefixPax,
  ]);

  // Child fields (Pax)
  const baseFareChdPax = watch(`${prefixPax}.baseFare.chd1`, 0);
  const gstHstChdPax = watch(`${prefixPax}.gstHst.chd1`, 0);
  const taxesChdPax = watch(`${prefixPax}.taxes.chd1`, 0);
  const totalChdPax = watch(`${passengersprefixPax}.chd`, 1);
  const subTotalChdPax = watch(`${prefixPax}.subTotal.chd1`, 0);

  useEffect(() => {
    const newSubTotal = calculateSubTotal(
      baseFareChdPax,
      gstHstChdPax,
      taxesChdPax,
      totalChdPax
    );
    setValue(`${prefixPax}.subTotal.chd1`, newSubTotal);
  }, [
    baseFareChdPax,
    gstHstChdPax,
    taxesChdPax,
    totalChdPax,
    setValue,
    prefixPax,
  ]);

  // Infant fields (Pax)
  const baseFareInfPax = watch(`${prefixPax}.baseFare.inf1`, 0);
  const gstHstInfPax = watch(`${prefixPax}.gstHst.inf1`, 0);
  const taxesInfPax = watch(`${prefixPax}.taxes.inf1`, 0);
  const totalInfPax = watch(`${passengersprefixPax}.inf`, 1);
  const subTotalInfPax = watch(`${prefixPax}.subTotal.inf1`, 0);

  useEffect(() => {
    const newSubTotal = calculateSubTotal(
      baseFareInfPax,
      gstHstInfPax,
      taxesInfPax,
      totalInfPax
    );
    setValue(`${prefixPax}.subTotal.inf1`, newSubTotal);
  }, [
    baseFareInfPax,
    gstHstInfPax,
    taxesInfPax,
    totalInfPax,
    setValue,
    prefixPax,
  ]);

  // Overall total for Pax
  useEffect(() => {
    const newTotal = roundToTwo(
      (Number(subTotalAdtPax) || 0) +
        (Number(subTotalChdPax) || 0) +
        (Number(subTotalInfPax) || 0)
    );
    setValue(`${prefixPax}.total`, newTotal);
  }, [subTotalAdtPax, subTotalChdPax, subTotalInfPax, setValue, prefixPax]);

  // Supplier Calculations
  const prefixSupplier = AirTickettoSupplier
    ? "airticket.airticket_to_supplier"
    : insuranceSupplier
    ? "insurance.insurance_to_supplier"
    : landPackageSupplier
    ? "land_package.landpackage_to_supplier"
    : cruiseSupplier
    ? "cruise.cruise_to_supplier"
    : hotelSupplier
    ? "hotel.hotel_to_supplier"
    : "";

  const supplierFinalKey = AirTickettoSupplier
    ? "supplier_id"
    : insuranceSupplier
    ? "insurance.insurance_supplier_id"
    : landPackageSupplier
    ? "land_package.land_package_supplier_id"
    : cruiseSupplier
    ? "cruise.cruise_supplier_id"
    : hotelSupplier
    ? "hotel.hotel_supplier_id"
    : "";

  const passengersprefixSupplier = AirTickettoSupplier
    ? "airticket.passengers"
    : insuranceSupplier
    ? "insurance.passengers"
    : landPackageSupplier
    ? "land_package.passengers"
    : cruiseSupplier
    ? "cruise.passengers"
    : hotelSupplier
    ? "hotel.passengers"
    : "";

  const totalSupplier = watch(`${prefixSupplier}.total`);

  // Adult Calculation for Supplier
  const baseFareAdtSupplier = watch(`${prefixSupplier}.baseFare.adt1`, 0);
  const gstHstAdtSupplier = watch(`${prefixSupplier}.gstHst.adt1`, 0);
  const taxesAdtSupplier = watch(`${prefixSupplier}.taxes.adt1`, 0);
  const totalAdtSupplier = watch(`${passengersprefixSupplier}.adt`, 1);
  const subTotalAdtSupplier = watch(`${prefixSupplier}.subTotal.adt1`, 0);

  useEffect(() => {
    const newSubTotal = calculateSubTotal(
      baseFareAdtSupplier,
      gstHstAdtSupplier,
      taxesAdtSupplier,
      totalAdtSupplier
    );
    setValue(`${prefixSupplier}.subTotal.adt1`, newSubTotal);
  }, [
    baseFareAdtSupplier,
    gstHstAdtSupplier,
    taxesAdtSupplier,
    totalAdtSupplier,
    setValue,
    prefixSupplier,
  ]);

  // Child Calculation for Supplier
  const baseFareChdSupplier = watch(`${prefixSupplier}.baseFare.chd1`, 0);
  const gstHstChdSupplier = watch(`${prefixSupplier}.gstHst.chd1`, 0);
  const taxesChdSupplier = watch(`${prefixSupplier}.taxes.chd1`, 0);
  const totalChdSupplier = watch(`${passengersprefixSupplier}.chd`, 1);
  const subTotalChdSupplier = watch(`${prefixSupplier}.subTotal.chd1`, 0);

  useEffect(() => {
    const newSubTotal = calculateSubTotal(
      baseFareChdSupplier,
      gstHstChdSupplier,
      taxesChdSupplier,
      totalChdSupplier
    );
    setValue(`${prefixSupplier}.subTotal.chd1`, newSubTotal);
  }, [
    baseFareChdSupplier,
    gstHstChdSupplier,
    taxesChdSupplier,
    totalChdSupplier,
    setValue,
    prefixSupplier,
  ]);

  // Infant Calculation for Supplier
  const baseFareInfSupplier = watch(`${prefixSupplier}.baseFare.inf1`, 0);
  const gstHstInfSupplier = watch(`${prefixSupplier}.gstHst.inf1`, 0);
  const taxesInfSupplier = watch(`${prefixSupplier}.taxes.inf1`, 0);
  const totalInfSupplier = watch(`${passengersprefixSupplier}.inf`, 1);
  const subTotalInfSupplier = watch(`${prefixSupplier}.subTotal.inf1`, 0);

  useEffect(() => {
    const newSubTotal = calculateSubTotal(
      baseFareInfSupplier,
      gstHstInfSupplier,
      taxesInfSupplier,
      totalInfSupplier
    );
    setValue(`${prefixSupplier}.subTotal.inf1`, newSubTotal);
  }, [
    baseFareInfSupplier,
    gstHstInfSupplier,
    taxesInfSupplier,
    totalInfSupplier,
    setValue,
    prefixSupplier,
  ]);

  // Overall total for Supplier
  useEffect(() => {
    const newTotalSupplier = roundToTwo(
      (Number(subTotalAdtSupplier) || 0) +
        (Number(subTotalChdSupplier) || 0) +
        (Number(subTotalInfSupplier) || 0)
    );
    setValue(`${prefixSupplier}.total`, newTotalSupplier);
  }, [
    subTotalAdtSupplier,
    subTotalChdSupplier,
    subTotalInfSupplier,
    setValue,
    prefixSupplier,
  ]);

  // update prefixSupplier to taxesAdtPax
  useEffect(() => {
    setValue(`${prefixSupplier}.taxes.adt1`, taxesAdtPax);
  }, [taxesAdtPax]);

  useEffect(() => {
    setValue(`${prefixSupplier}.taxes.chd1`, taxesChdPax);
  }, [taxesChdPax]);

  useEffect(() => {
    setValue(`${prefixSupplier}.taxes.inf1`, taxesInfPax);
  }, [taxesInfPax]);

  // Commission calculation for Pax
  useEffect(() => {
    const commission = roundToTwo(
      (Number(totalPax) || 0) - (Number(totalSupplier) || 0)
    );
    setValue(`${prefixPax}.commission`, commission);
  }, [totalSupplier, totalPax, setValue, prefixPax]);

  // auto set paid amount
  // Watch the payment received checkbox
  const payment_received_from_pax = watch(
    `${prefixPax}.payment_received_from_pax`
  );
  const amountPaid = watch(`${prefixPax}.amountPaid`);

  // 👇 Only this useEffect is needed
  useEffect(() => {
    if (!prefixPax) return;

    const totalpaxbalance = Number(watch(`${prefixPax}.balance`)) || 0;
    const isPaidOrOverpaid = totalpaxbalance <= 0;

    setValue(`${prefixPax}.payment_received_from_pax`, isPaidOrOverpaid);
  }, [watch(`${prefixPax}.balance`), prefixPax, setValue]);

  // supplier balance calculation start
  const commissionFromSupplier = watch(
    `${prefixSupplier}.commission_from_supplier`
  );
  const paymentMadeSupplier = watch(`${prefixSupplier}.paymentMade`);

  useEffect(() => {
    const receivedValue = commissionFromSupplier
      ? Math.round((paymentMadeSupplier - totalSupplier) * 100) / 100
      : 0;

    setValue(`${prefixSupplier}.payment_received_from_supplier`, receivedValue);
  }, [
    commissionFromSupplier,
    paymentMadeSupplier,
    // updateData,
    totalSupplier,
    totalPax,
  ]);
  // supplier balance calculation end

  // set refund into supplier with pax refund
  // const refundReceivedFromSupplier = watch(
  //   `${prefixSupplier}.refund_received_from_supplier`
  // );

  // useEffect(() => {
  //   if (refundReceivedFromSupplier) {
  //     setValue(`${prefixSupplier}.refund`, totalRefundPax);
  //   } else {
  //     setValue(`${prefixSupplier}.refund`, 0);
  //   }
  // }, [refundReceivedFromSupplier]);

  useEffect(() => {
    getTransactionType();
  }, []);


  // INSOURENCE PASSENGER ISSUE
  useEffect(() => {
    if (!updateData) {
      passengerTypes.forEach((passenger) => {
        const path = `${passengersprefixPax}.${passenger.id}`;
        const currentValue = getValues(path);
        if (currentValue === undefined) {
          setValue(path, passenger.id === "adt" ? 1 : 0);
        }
      });
    }
  }, [passengersprefixPax]);

  useEffect(() => {
    // If updateData doesn't exist or paymentMethodData is empty, exit early
    if (!updateData || paymentMethodData.length === 0) return;
    // airticket
    setValue(
      `airticket.airticket_from_pax.transation_type_id`,
      Number(updateData?.airticket?.airticket_from_pax?.transation_type_id)
    );
    setValue(
      `airticket.airticket_to_supplier.transation_type_id`,
      Number(updateData?.airticket?.airticket_to_supplier?.transation_type_id)
    );

    // insurence
    setValue(
      `insurance.insurance_from_pax.transation_type_id`,
      updateData?.insurance?.insurance_from_pax?.transation_type_id
    );
    setValue(
      `insurance.insurance_to_supplier.transation_type_id`,
      Number(updateData?.insurance?.insurance_to_supplier?.transation_type_id)
    );

    // land package
    setValue(
      `land_package.landpackage_from_pax.transation_type_id`,
      updateData?.land_package?.landpackage_from_pax?.transation_type_id
    );
    setValue(
      `land_package.landpackage_to_supplier.transation_type_id`,
      updateData?.land_package?.landpackage_to_supplier?.transation_type_id
    );

    // cruise
    setValue(
      `cruise.cruise_from_pax.transation_type_id`,
      updateData?.cruise?.cruise_from_pax?.transation_type_id
    );
    setValue(
      `cruise.cruise_to_supplier.transation_type_id`,
      updateData?.cruise?.cruise_to_supplier?.transation_type_id
    );

    // hotel
    setValue(
      `hotel.hotel_from_pax.transation_type_id`,
      updateData?.hotel?.hotel_from_pax?.transation_type_id
    );
    setValue(
      `hotel.hotel_to_supplier.transation_type_id`,
      updateData?.hotel?.hotel_to_supplier?.transation_type_id
    );

    const toBoolean = (value) => String(value).toLowerCase() === "true";

    // Then use it:
    setValue(
      "valid_canadian_passport",
      toBoolean(updateData?.valid_canadian_passport)
    );
    setValue(
      "canadian_citizenship_or_prCard",
      toBoolean(updateData?.canadian_citizenship_or_prCard)
    );
    setValue("valid_travel_visa", toBoolean(updateData?.valid_travel_visa));
    setValue("tourist_card", toBoolean(updateData?.tourist_card));

    setValue("special_remarks", updateData?.special_remarks);
    setValue("other_remarks", updateData?.other_remarks);
  }, [updateData, paymentMethodData]);

  return (
    <div>
      <div className="bg-[#09c8ff] p-2 text-white">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="md:whitespace-nowrap">Supplier:</label>
          </div>
          <div className="w-96">
            <select
              {...register(supplierFinalKey)}
              value={watch(supplierFinalKey)}
              onChange={(e) => {
                setValue(supplierFinalKey, e.target.value);
                clearErrors(supplierFinalKey);
              }}
              className="w-full bg-white text-gray-900"
            >
              <option value="">Select...</option>
              {supplierData.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Error display logic */}
            {AirTickettoSupplier
              ? errors?.supplier_id && (
                  <span className="text-red-500">
                    {errors?.supplier_id?.message}
                  </span>
                )
              : supplierFinalKey &&
                errors?.[supplierFinalKey.split(".")[0]]?.[
                  supplierFinalKey.split(".")[1]
                ]?.supplier_id && (
                  <span className="text-red-500">
                    {
                      errors?.[supplierFinalKey.split(".")[0]]?.[
                        supplierFinalKey.split(".")[1]
                      ]?.supplier_id?.message
                    }
                  </span>
                )}
          </div>
        </div>
      </div>

      <div className="grid h-full grid-cols-1 overflow-hidden sm:grid-cols-2">
        {/* Payment Details from Pax */}
        <div>
          <h2 className="bg-[#d7b53f] px-2 text-sm text-black">
            Payment Details from Pax
          </h2>
          <div className="h-full bg-[#eee0af] px-2 pt-1 text-sm text-black">
            {/* Passenger Counts */}
            <div className="mb-2">
              <div className="grid grid-cols-4 justify-end gap-4">
                <div></div>
                {passengerTypes.map((passenger) => {
                  // Determine the range based on passenger type.
                  const options =
                    passenger.id === "adt"
                      ? [...Array(10)].map((_, i) => i + 1) // 1 to 10
                      : [...Array(11)].map((_, i) => i); // 0 to 10

                  return (
                    <div key={passenger.id} className="flex items-center gap-2">
                      <label>{passenger.label}</label>
                      <select
                        id={passenger.id}
                        {...register(`${passengersprefixPax}.${passenger.id}`)}
                        value={
                          watch(`${passengersprefixPax}.${passenger.id}`) ??
                          (passenger.id === "adt" ? 1 : 0)
                        } // ✅ controlled value
                        className="w-full cursor-pointer bg-white"
                      >
                        {options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Base Fare */}
            <div className="mb-2">
              <div className="grid grid-cols-4 gap-4">
                <h3>Base Fare</h3>
                <input
                  step="any"
                  type="number"
                  {...register(`${prefixPax}.baseFare.adt1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  step="any"
                  type="number"
                  {...register(`${prefixPax}.baseFare.chd1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  step="any"
                  type="number"
                  {...register(`${prefixPax}.baseFare.inf1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
              </div>
            </div>
            {/* GST/HST (if applicable) */}
            {airTicket && (
              <div className="mb-2">
                <div className="grid grid-cols-4 gap-4">
                  <h3>GST/HST</h3>
                  <input
                    step="any"
                    type="number"
                    {...register(`airticket.airticket_from_pax.gstHst.adt1`, {
                      valueAsNumber: true,
                    })}
                    className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                  />
                  <input
                    step="any"
                    type="number"
                    {...register("airticket.airticket_from_pax.gstHst.chd1", {
                      valueAsNumber: true,
                    })}
                    className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                  />
                  <input
                    step="any"
                    type="number"
                    {...register("airticket.airticket_from_pax.gstHst.inf1", {
                      valueAsNumber: true,
                    })}
                    className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                  />
                </div>
              </div>
            )}
            {/* Taxes */}
            <div className="mb-2">
              <div className="grid grid-cols-4 gap-4">
                <h3>Taxes</h3>
                <input
                  step="any"
                  type="number"
                  {...register(`${prefixPax}.taxes.adt1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  step="any"
                  type="number"
                  {...register(`${prefixPax}.taxes.chd1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  step="any"
                  type="number"
                  {...register(`${prefixPax}.taxes.inf1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
              </div>
            </div>
            {/* Calculated SubTotal */}
            <div className="mb-2">
              <div className="grid grid-cols-4 gap-4">
                <h3>SubTotal</h3>
                <input
                  step="any"
                  type="number"
                  readOnly
                  {...register(`${prefixPax}.subTotal.adt1`)}
                  value={formatDecimal(watch(`${prefixPax}.subTotal.adt1`))}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  step="any"
                  type="number"
                  readOnly
                  {...register(`${prefixPax}.subTotal.chd1`)}
                  value={formatDecimal(watch(`${prefixPax}.subTotal.chd1`))}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  step="any"
                  type="number"
                  readOnly
                  {...register(`${prefixPax}.subTotal.inf1`)}
                  value={formatDecimal(watch(`${prefixPax}.subTotal.inf1`))}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
              </div>
            </div>
            {/* Total */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer font-medium">Total</label>
              <input
                id="totel"
                type="number"
                readOnly
                {...register(`${prefixPax}.total`)}
                value={formatDecimal(watch(`${prefixPax}.total`))}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 font-semibold text-black"
              />
            </div>
            {/* Amount Paid */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Amount Paid</label>
              <input
                id="amount_paid"
                type="number"
                step="any"
                {...register(`${prefixPax}.amountPaid`, {
                  valueAsNumber: true,
                })}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
              />
            </div>
            {/* Balance */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Balance:</label>
              <input
                id="balance"
                readOnly
                type="number"
                {...register(`${prefixPax}.balance`, { valueAsNumber: true })}
                value={formatDecimal(watch(`${prefixPax}.balance`))}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
              />
            </div>
            {/* Payment Method */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Payment Method</label>
              <select
                id="payment_method"
                {...register(`${prefixPax}.transation_type_id`)}
                className={`!rounded-none border bg-white !px-1 !py-0 text-black `}
                onChange={() => clearErrors(`${prefixPax}.transation_type_id`)}
              >
                <option value="">Select Payment Method</option>
                {paymentMethodData.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Commission */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Commission</label>
              <input
                type="number"
                readOnly
                id="commission"
                {...register(`${prefixPax}.commission`, {
                  valueAsNumber: true,
                })}
                value={formatDecimal(watch(`${prefixPax}.commission`))}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
              />
            </div>

            {/* {updateData && ( */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Refund</label>
              <input
                type="number"
                id="refund"
                {...register(`${prefixPax}.refund`, { valueAsNumber: true })}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
              />
            </div>
            {/* )} */}

            {/* {totalpaxbalance < 0 && ( */}
            <div className="group relative mb-2 flex justify-between">
              <label className="cursor-pointer">Refund Paid to client</label>

              <input
                type="number"
                id="refund_paid"
                {...register(`${prefixPax}.refund_paid`)}
                // disabled={totalpaxbalance >= 0}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black disabled:cursor-not-allowed"
              />

              {/* {totalpaxbalance >= 0 && (
              <div className="absolute -top-8 right-0 z-10 hidden w-64 rounded bg-black px-2 py-1 text-xs text-white group-hover:block">
                Balance is 0, you don't need to pay to client.
              </div>
            )} */}
            </div>

            {/* )} */}

            {/* Payment Received from Pax */}
            <div className="group relative flex items-center justify-between">
              <label className="cursor-pointer">
                Payment Received from Pax:
              </label>

              <input
                type="checkbox"
                className="cursor-not-allowed"
                id="payment_received_from_pax"
                disabled
                {...register(`${prefixPax}.payment_received_from_pax`)}
              />

              {/* Tooltip shown on hover if checkbox is disabled */}
              <div
                className={`
      absolute -top-1.5 right-10 z-10 hidden rounded px-2 py-1 text-xs 
      text-white group-hover:block
      ${
        watch(`${prefixPax}.payment_received_from_pax`)
          ? "bg-green-600"
          : "bg-red-600"
      }
    `}
              >
                {watch(`${prefixPax}.payment_received_from_pax`)
                  ? "Payment received"
                  : "Payment pending"}
              </div>
            </div>
          </div>
        </div>
        {/* Payment Details to Supplier */}
        <div>
          <h2 className="bg-[#6fc266] px-2 text-sm text-black">
            Payment Details to Supplier
          </h2>
          <div className="h-full bg-[#c8dcc6] px-2 pt-1 text-sm text-black">
            {/* Passenger Counts */}
            <div className="mb-2">
              <div className="grid grid-cols-4 gap-4">
                <div></div>
                {passengerTypes.map((passenger) => {
                  // Create options based on the passenger type.
                  const options =
                    passenger.id === "adt"
                      ? [...Array(10)].map((_, i) => i + 1) // 1 to 10 for adults
                      : [...Array(11)].map((_, i) => i); // 0 to 10 for chd/inf

                  return (
                    <div key={passenger.id} className="flex items-center gap-2">
                      <label>{passenger.label}</label>
                      <select
                        id={passenger.id}
                        className="w-full cursor-not-allowed bg-white"
                        disabled={true}
                        value={watch(
                          `${passengersprefixSupplier}.${passenger.id}`
                        )}
                      >
                        {options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Base Fare */}
            <div className="mb-2">
              <div className="grid grid-cols-4 gap-4">
                <h3>Base Fare</h3>
                <input
                  type="number"
                  step="any"
                  {...register(`${prefixSupplier}.baseFare.adt1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  type="number"
                  step="any"
                  {...register(`${prefixSupplier}.baseFare.chd1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  type="number"
                  step="any"
                  {...register(`${prefixSupplier}.baseFare.inf1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
              </div>
            </div>
            {/* GST/HST (if applicable) */}
            {airTicket && (
              <div className="mb-2">
                <div className="grid grid-cols-4 gap-4">
                  <h3>GST/HST</h3>
                  <input
                    step="any"
                    type="number"
                    {...register(`${prefixSupplier}.gstHst.adt1`, {
                      valueAsNumber: true,
                    })}
                    className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                  />
                  <input
                    step="any"
                    type="number"
                    {...register(`${prefixSupplier}.gstHst.chd1`, {
                      valueAsNumber: true,
                    })}
                    className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                  />
                  <input
                    step="any"
                    type="number"
                    {...register(`${prefixSupplier}.gstHst.inf1`, {
                      valueAsNumber: true,
                    })}
                    className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                  />
                </div>
              </div>
            )}
            {/* Taxes */}
            <div className="mb-2">
              <div className="grid grid-cols-4 gap-4">
                <h3>Taxes</h3>
                <input
                  type="number"
                  step="any"
                  {...register(`${prefixSupplier}.taxes.adt1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  type="number"
                  step="any"
                  {...register(`${prefixSupplier}.taxes.chd1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  type="number"
                  step="any"
                  {...register(`${prefixSupplier}.taxes.inf1`, {
                    valueAsNumber: true,
                  })}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
              </div>
            </div>
            {/* Calculated SubTotal */}
            <div className="mb-2">
              <div className="grid grid-cols-4 gap-4">
                <h3>SubTotal</h3>
                <input
                  type="number"
                  step="any"
                  readOnly
                  {...register(`${prefixSupplier}.subTotal.adt1`)}
                  value={formatDecimal(
                    watch(`${prefixSupplier}.subTotal.adt1`)
                  )}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  type="number"
                  step="any"
                  readOnly
                  {...register(`${prefixSupplier}.subTotal.chd1`)}
                  value={formatDecimal(
                    watch(`${prefixSupplier}.subTotal.chd1`)
                  )}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
                <input
                  type="number"
                  step="any"
                  readOnly
                  {...register(`${prefixSupplier}.subTotal.inf1`)}
                  value={formatDecimal(
                    watch(`${prefixSupplier}.subTotal.inf1`)
                  )}
                  className="w-full !rounded-none border bg-white !px-1 !py-0 text-black"
                />
              </div>
            </div>
            {/* Total */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer font-medium">Total</label>
              <input
                id="total_supplier"
                type="number"
                readOnly
                {...register(`${prefixSupplier}.total`)}
                value={formatDecimal(watch(`${prefixSupplier}.total`))}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 font-semibold text-black"
              />
            </div>
            {/* Payment Method */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Payment Method</label>
              <select
                id="payment_method_supplier"
                {...register(`${prefixSupplier}.transation_type_id`)}
                className={`!rounded-none border bg-white !px-1 !py-0 text-black outline-none `}
                onChange={() =>
                  clearErrors(`${prefixSupplier}.transation_type_id`)
                }
              >
                <option value="">Select Payment Method</option>
                {paymentMethodData.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Payment Made */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Payment Made to Supplier</label>
              <input
                step="any"
                id="payment_made_supplier"
                type="number"
                {...register(`${prefixSupplier}.paymentMade`)}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
              />
            </div>
            {/* Commission Received */}
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Commission from Supplier</label>
              <input
                step="any"
                id="commission_received_supplier"
                type="number"
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
                {...register(
                  `${prefixSupplier}.payment_received_from_supplier`
                )}
              />
            </div>
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">
                Recall commission to suppiler
              </label>
              <input
                step="any"
                id="recall_commission_to_supplier"
                type="number"
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
                {...register(`${prefixSupplier}.recall_commission_to_supplier`)}
              />
            </div>
            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Refund from supplier</label>
              <input
                step="any"
                type="number"
                id="refund_supplier"
                {...register(`${prefixSupplier}.refund`, {
                  valueAsNumber: true,
                })}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
              />
            </div>

            <div className="mb-2 flex justify-between">
              <label className="cursor-pointer">Cash in hand</label>
              <input
                step="any"
                type="number"
                id="cash_in_hand"
                {...register(`${prefixSupplier}.cash_in_hand`, {
                  valueAsNumber: true,
                })}
                className="!w-24 !rounded-none border bg-white !px-1 !py-0 text-black"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="cursor-pointer">
                Commission Received from Supplier:
              </label>
              <input
                type="checkbox"
                className="cursor-pointer"
                id="commission_from_supplier"
                {...register(`${prefixSupplier}.commission_from_supplier`)}
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="cursor-pointer">
                Refund Received from Supplier:
              </label>
              <input
                type="checkbox"
                className="cursor-pointer"
                id="refund_received_from_supplier"
                {...register(`${prefixSupplier}.refund_received_from_supplier`)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
